import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  adminExchangeRateService,
  UpdateExchangeRateRequest,
  ExchangeRateHistoryFilters,
} from '@/services/admin-exchange-rate.service'
import { toast } from 'sonner'
import { AxiosError } from 'axios'

/**
 * React Query hooks for exchange rate management.
 *
 * Provides hooks for fetching current rates, history, and updating rates.
 * Includes automatic cache invalidation and optimistic updates.
 */

/**
 * Fetches all current exchange rates.
 * Auto-refreshes every 60 seconds to keep rates current.
 *
 * @returns Query result with current exchange rates
 */
export function useExchangeRates() {
  return useQuery({
    queryKey: ['admin', 'exchange-rates'],
    queryFn: () => adminExchangeRateService.getExchangeRates(),
    staleTime: 30000, // Consider data stale after 30 seconds
    refetchInterval: 60000, // Auto-refresh every 60 seconds
    refetchOnWindowFocus: true, // Refresh when user returns to tab
  })
}

/**
 * Fetches a specific exchange rate by currency code.
 *
 * @param currency - Currency code (e.g., "USD")
 * @returns Query result with the exchange rate
 */
export function useExchangeRate(currency: string) {
  return useQuery({
    queryKey: ['admin', 'exchange-rates', currency],
    queryFn: () => adminExchangeRateService.getExchangeRate(currency),
    enabled: !!currency, // Only fetch if currency is provided
    staleTime: 30000,
  })
}

/**
 * Fetches exchange rate history with filters.
 *
 * @param filters - History filters including currency, date range, and pagination
 * @returns Query result with historical exchange rates
 */
export function useExchangeRateHistory(filters: ExchangeRateHistoryFilters) {
  return useQuery({
    queryKey: ['admin', 'exchange-rates', filters.currency, 'history', filters],
    queryFn: () => adminExchangeRateService.getExchangeRateHistory(filters),
    enabled: !!filters.currency, // Only fetch if currency is provided
    staleTime: 60000, // History doesn't change as often
  })
}

/**
 * Fetches supported currencies list.
 *
 * @returns Query result with array of currency codes
 */
export function useSupportedCurrencies() {
  return useQuery({
    queryKey: ['admin', 'exchange-rates', 'currencies'],
    queryFn: () => adminExchangeRateService.getSupportedCurrencies(),
    staleTime: 3600000, // Currencies list rarely changes (1 hour)
  })
}

/**
 * Mutation hook for updating an exchange rate.
 * Invalidates relevant queries on success.
 *
 * @returns Mutation result
 */
export function useUpdateExchangeRate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      currency,
      request,
    }: {
      currency: string
      request: UpdateExchangeRateRequest
    }) => adminExchangeRateService.updateExchangeRate(currency, request),
    onSuccess: (data, variables) => {
      // Invalidate all exchange rate queries
      queryClient.invalidateQueries({ queryKey: ['admin', 'exchange-rates'] })

      // Invalidate history for this specific currency
      queryClient.invalidateQueries({
        queryKey: ['admin', 'exchange-rates', variables.currency, 'history'],
      })

      toast.success('Exchange Rate Updated', {
        description: `${variables.currency} rate updated to ${data.displayRate}`,
      })
    },
    onError: (error: AxiosError | Error) => {
      let errorMessage = 'Failed to update exchange rate'

      if (error instanceof AxiosError) {
        const apiError = error.response?.data
        if (apiError && typeof apiError === 'object' && 'error' in apiError) {
          errorMessage = (apiError as { error: { message: string } }).error.message
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message
        }
      } else {
        errorMessage = error.message
      }

      toast.error('Update Failed', {
        description: errorMessage,
      })
    },
  })
}

/**
 * Combined hook for all exchange rate queries and mutations.
 * Provides a single interface for exchange rate management.
 *
 * @returns Object with all exchange rate query and mutation functions
 */
export function useExchangeRateQueries() {
  const queryClient = useQueryClient()

  const exchangeRates = useExchangeRates()
  const updateExchangeRate = useUpdateExchangeRate()

  return {
    // Queries
    exchangeRates,

    // Mutations
    updateExchangeRate,

    // Helper methods
    refetchExchangeRates: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'exchange-rates'] })
    },
  }
}
